/**
 * @fileoverview Shared localStorage keys and layout helpers for admin DataGrid templates.
 * @module ui-admin/DataGrid/gridPersistence
 *
 * Responsibilities:
 * - Own the localStorage key contract used by DataGrid (column visibility/order/contextual overrides)
 *   and GridPageShell / page-level template modals (autosave).
 * - Persist an applied template or factory layout to every layout key in one shot
 *   so remount cannot rehydrate a stale column arrangement from a second layer.
 * - Clear every layout key on "Reset to defaults", then restore the page factory
 *   layout (not the starred template).
 *
 * Dependencies:
 * - types.GridState / GridTemplate
 *
 * Architecture:
 * - Presentation-layer persistence only (browser localStorage). No Dexie, no API.
 * - DataGrid writes `colvis`/`colorder` and contextual override markers independently
 *   of GridPageShell's `grid_autosave_*` snapshot. Apply/reset keep each layer aligned
 *   so the empty initial state is not treated as "not provided" and old columns return.
 *
 * Notes:
 * - LocationsPage historically used `datagrid_${gridId}_autosave` instead of
 *   `grid_autosave_${gridId}`; pass that key via extraAutosaveKeys.
 * - "Reset to defaults" uses resolveFactoryGridState (page defaults only).
 *   resolveDefaultGridState still prefers a starred/system template and is not
 *   the Reset path. Mount uses initializePersistedGridState: leftover autosave
 *   unless a pending starred default (Star without Apply) is waiting.
 * - Saving a named template must read live colvis/colorder (DataGrid writes
 *   those immediately) rather than only the parent autosave snapshot, which
 *   is debounced 500ms and can miss a just-closed Columns dialog.
 * - Visibility maps that only list hidden columns (`undefined` = visible) cannot
 *   restore a one-column view after a later "show all". resolveTemplateColumnVisibility
 *   binds one named template to its own true/false map so Default View cannot
 *   inherit T1's live columns.
 * - GridState also carries `columnFilters` (per-column filter-row values) so a
 *   column filter survives remount and shows in the active-filter chips bar.
 * - Saved layout entries for columns not yet supplied during a mount remain pending;
 *   columns supplied and later retired during that mount are pruned.
 */
import type { GridState, GridTemplate } from './types';

/**
 * @storage
 * Type: LocalStorage
 * Schema: `grid_autosave_{gridId}` → JSON GridState snapshot
 * Sync Role: client-only layout preference; not replicated
 */
export function gridAutosaveKey(gridId: string): string {
  return `grid_autosave_${gridId}`;
}

/**
 * @storage
 * Type: LocalStorage
 * Schema: `datagrid_{gridId}_colvis` → JSON Record<columnId, boolean>
 * Sync Role: client-only layout preference; not replicated
 */
export function gridColumnVisibilityKey(gridId: string): string {
  return `datagrid_${gridId}_colvis`;
}

/**
 * @storage
 * Type: LocalStorage
 * Schema: `datagrid_{gridId}_contextual_colvis_overrides` → JSON Record<contextualColumnId, boolean>
 * Sync Role: client-only explicit overrides for context-driven column defaults; not replicated
 */
export function gridContextualColumnVisibilityOverridesKey(gridId: string): string {
  return `datagrid_${gridId}_contextual_colvis_overrides`;
}

/**
 * @process readContextualColumnVisibilityOverrides
 * Steps: Read and validate the explicit contextual-column override map.
 * Inputs: gridId.
 * Outputs: Boolean override map, or `{}` when missing/corrupt.
 * Failure Handling: Storage and parse errors return `{}`.
 * Idempotency: REQUIRED.
 */
export function readContextualColumnVisibilityOverrides(gridId: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(gridContextualColumnVisibilityOverridesKey(gridId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'),
    );
  } catch { /* ignore */ }
  return {};
}

/**
 * @process writeContextualColumnVisibilityOverrides
 * Steps: Persist boolean overrides, removing the key when no explicit choices remain.
 * Inputs: gridId, contextual-column override map.
 * Outputs: void.
 * Failure Handling: Swallow Storage errors.
 * Idempotency: REQUIRED — last write wins.
 */
export function writeContextualColumnVisibilityOverrides(
  gridId: string,
  overrides: Record<string, boolean>,
): void {
  const valid = Object.fromEntries(
    Object.entries(overrides).filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'),
  );
  try {
    if (Object.keys(valid).length === 0) localStorage.removeItem(gridContextualColumnVisibilityOverridesKey(gridId));
    else localStorage.setItem(gridContextualColumnVisibilityOverridesKey(gridId), JSON.stringify(valid));
  } catch { /* ignore quota / private mode */ }
}

/**
 * @process applyContextualColumnDefaults
 * Steps: For each supplied contextual column, use its explicit override or its current default.
 * Inputs: Complete supplied visibility map, contextual defaults, optional user overrides.
 * Outputs: Visibility map with contextual values resolved without changing other columns.
 * Failure Handling: Unknown contextual IDs are ignored.
 * Idempotency: REQUIRED (pure).
 */
export function applyContextualColumnDefaults(
  visibility: Record<string, boolean>,
  defaults: Record<string, boolean> | undefined,
  overrides: Record<string, boolean> = {},
): Record<string, boolean> {
  if (!defaults) return visibility;
  let resolved: Record<string, boolean> | undefined;
  for (const [id, defaultValue] of Object.entries(defaults)) {
    if (!Object.prototype.hasOwnProperty.call(visibility, id)) continue;
    const value = Object.prototype.hasOwnProperty.call(overrides, id) ? overrides[id] : defaultValue;
    if (visibility[id] !== value) {
      resolved ??= { ...visibility };
      resolved[id] = value;
    }
  }
  return resolved ?? visibility;
}

/**
 * @storage
 * Type: LocalStorage
 * Schema: `datagrid_{gridId}_colorder` → JSON string[] of column ids
 * Sync Role: client-only layout preference; not replicated
 */
export function gridColumnOrderKey(gridId: string): string {
  return `datagrid_${gridId}_colorder`;
}

/**
 * @storage
 * Type: LocalStorage
 * Schema: `grid_applied_template_{gridId}` → `{ id, name }`
 * Sync Role: client-only UI hint for which named template is on screen
 */
export function gridAppliedTemplateKey(gridId: string): string {
  return `grid_applied_template_${gridId}`;
}

/**
 * @process readAppliedTemplate
 * Steps: 1. Read the applied-template hint for this grid.
 *        2. Return id+name when the JSON is valid.
 * Inputs: gridId.
 * Outputs: `{ id, name }` or null.
 * Failure Handling: missing/corrupt → null.
 * Idempotency: REQUIRED.
 */
export function readAppliedTemplate(gridId: string): { id: string; name: string } | null {
  try {
    const raw = localStorage.getItem(gridAppliedTemplateKey(gridId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; name?: string };
    if (typeof parsed?.name === 'string' && parsed.name.trim()) {
      return { id: typeof parsed.id === 'string' ? parsed.id : '', name: parsed.name };
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * @process writeAppliedTemplate
 * Steps: 1. Remove the hint when tpl is null (factory reset).
 *        2. Otherwise persist id+name.
 * Inputs: gridId, tpl or null.
 * Outputs: void.
 * Failure Handling: swallow Storage errors.
 * Idempotency: REQUIRED.
 */
export function writeAppliedTemplate(
  gridId: string,
  tpl: { id: string; name: string } | null,
): void {
  try {
    if (!tpl) localStorage.removeItem(gridAppliedTemplateKey(gridId));
    else localStorage.setItem(gridAppliedTemplateKey(gridId), JSON.stringify(tpl));
  } catch { /* ignore quota / private mode */ }
}

/**
 * @contract AppliedGridTemplateMeta
 * Purpose: Named template currently on screen, or null after factory reset.
 * Fields: `{ id, name }` when applying a template; `null` when resetting.
 * Rules: `undefined` means "do not change the badge" (legacy apply-only callers).
 * Sync: localStorage hint via writeAppliedTemplate; not replicated.
 */
export type AppliedGridTemplateMeta = { id: string; name: string } | null | undefined;

/**
 * @process applyGridTemplateMeta
 * Steps: 1. Ignore undefined (no badge change).
 *        2. Persist id+name, or clear the hint when meta is null.
 *        3. Mirror that value into parent React state.
 * Inputs: gridId, apply/reset meta, setter for the displayed template name.
 * Outputs: void.
 * Failure Handling: storage errors are swallowed by writeAppliedTemplate.
 * Idempotency: REQUIRED.
 */
export function applyGridTemplateMeta(
  gridId: string,
  meta: AppliedGridTemplateMeta,
  setAppliedTemplateName: (name: string | null) => void,
): void {
  if (meta === undefined) return;
  writeAppliedTemplate(gridId, meta);
  setAppliedTemplateName(meta?.name ?? null);
}

/**
 * @storage
 * Type: LocalStorage
 * Schema: `grid_pending_default_{gridId}` → `{ id, name }`
 * Sync Role: one-shot bookmark written by Star; consumed on the next mount
 */
export function gridPendingDefaultKey(gridId: string): string {
  return `grid_pending_default_${gridId}`;
}

/**
 * @process readPendingDefault
 * Steps: 1. Read the pending starred-default bookmark.
 *        2. Return id+name when the JSON is valid.
 * Inputs: gridId.
 * Outputs: `{ id, name }` or null.
 * Failure Handling: missing/corrupt → null.
 * Idempotency: REQUIRED.
 */
export function readPendingDefault(gridId: string): { id: string; name: string } | null {
  try {
    const raw = localStorage.getItem(gridPendingDefaultKey(gridId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; name?: string };
    if (typeof parsed?.id === 'string' && parsed.id.trim() && typeof parsed?.name === 'string' && parsed.name.trim()) {
      return { id: parsed.id, name: parsed.name };
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * @process writePendingDefault
 * Steps: 1. Remove the bookmark when tpl is null (Apply, Reset, or unstar).
 *        2. Otherwise persist id+name for the next mount.
 * Inputs: gridId, tpl or null.
 * Outputs: void.
 * Failure Handling: swallow Storage errors.
 * Idempotency: REQUIRED.
 */
export function writePendingDefault(
  gridId: string,
  tpl: { id: string; name: string } | null,
): void {
  try {
    if (!tpl) localStorage.removeItem(gridPendingDefaultKey(gridId));
    else localStorage.setItem(gridPendingDefaultKey(gridId), JSON.stringify(tpl));
  } catch { /* ignore quota / private mode */ }
}

/**
 * @process formatTemplateColumnSummary
 * Steps: 1. Factory/empty maps (no real columns recorded) → "All columns".
 *        2. Otherwise count visible data columns for that named snapshot.
 * Inputs: template GridState, optional knownColumnIds.
 * Outputs: Short label such as "1 column" or "All columns".
 * Failure Handling: missing state → "All columns".
 * Idempotency: REQUIRED (pure).
 */
export function formatTemplateColumnSummary(
  state: Partial<GridState> | undefined,
  knownColumnIds: string[] = [],
): string {
  const vis = state?.columnVisibility ?? {};
  const dataEntries = Object.entries(vis).filter(([id]) => (
    !id.startsWith('sa_') && id !== '_actions' && id !== '_select'
  ));
  if (dataEntries.length === 0) return 'All columns';
  const known = knownColumnIds.filter((id) => (
    !id.startsWith('sa_') && id !== '_actions' && id !== '_select'
  ));
  const ids = known.length > 0 ? known : dataEntries.map(([id]) => id);
  const resolved = resolveTemplateColumnVisibility(vis, known.length > 0 ? knownColumnIds : ids);
  const visible = ids.filter((id) => resolved[id] !== false).length;
  return visible === 1 ? '1 column' : `${visible} columns`;
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore quota / private mode */ }
}

/**
 * @process cloneGridState
 * Steps: 1. Shallow-copy the GridState record.
 *        2. Deep-copy nested objects/arrays so later column edits cannot mutate
 *           a saved template or a just-applied snapshot in place.
 * Inputs: state — complete or partial GridState.
 * Outputs: A new GridState-shaped object with independent nested values.
 * Failure Handling: missing nested fields → empty object/array (never throws).
 * Idempotency: REQUIRED (pure).
 */
export function cloneGridState<T extends Partial<GridState>>(state: T): T {
  return {
    ...state,
    columnVisibility: { ...(state.columnVisibility ?? {}) },
    columnOrder: [...(state.columnOrder ?? [])],
    sorting: [...(state.sorting ?? [])],
    filterValues: { ...(state.filterValues ?? {}) },
    columnFilters: state.columnFilters ? state.columnFilters.map((f) => ({ ...f })) : state.columnFilters,
    groupByColumns: state.groupByColumns ? [...state.groupByColumns] : state.groupByColumns,
    activeQuery: state.activeQuery ? JSON.parse(JSON.stringify(state.activeQuery)) : state.activeQuery,
  };
}

/**
 * @process gridStatesEqual
 * Steps: 1. Compare every tracked GridState field by canonical JSON.
 * Inputs: a, b — complete or partial GridState snapshots.
 * Outputs: true when all tracked fields serialize identically.
 * Failure Handling: undefined/missing fields normalize to null on both sides.
 * Idempotency: REQUIRED (pure).
 * Notes: Field-wise comparison keeps key insertion order stable for states
 *   produced by the same grid. Used for applied-template drift detection —
 *   the label only survives while the live state matches the stored snapshot.
 */
export function gridStatesEqual(a: Partial<GridState>, b: Partial<GridState>): boolean {
  const FIELDS: (keyof GridState)[] = [
    'columnVisibility', 'columnOrder', 'sorting', 'filterValues',
    'pageSize', 'searchText', 'activeQuery', 'groupByColumns', 'columnFilters',
  ];
  // Missing and explicitly-empty values mean the same thing across the apply →
  // remount → onStateChange round-trip (e.g. `groupByColumns: []` vs absent),
  // so both normalize to null before comparison.
  const normalize = (v: unknown): unknown => {
    if (v === undefined || v === null || v === '') return null;
    if (Array.isArray(v)) return v.length === 0 ? null : v;
    if (typeof v === 'object' && Object.keys(v as object).length === 0) return null;
    return v;
  };
  return FIELDS.every(
    (k) => JSON.stringify(normalize(a[k])) === JSON.stringify(normalize(b[k])),
  );
}

/**
 * @process clearPersistedGridLayout
 * Steps: 1. Build the canonical key set (autosave + colvis + contextual overrides + colorder).
 *        2. Append any legacy extra autosave keys.
 *        3. removeItem each key; ignore quota / private-mode errors.
 * Inputs: gridId, optional extraAutosaveKeys (e.g. LocationsPage legacy key).
 * Outputs: void — localStorage keys removed.
 * Failure Handling: swallow Storage errors so reset still applies in-memory.
 * Idempotency: REQUIRED (removing missing keys is a no-op).
 */
export function clearPersistedGridLayout(
  gridId: string,
  extraAutosaveKeys: string[] = [],
): void {
  const keys = [
    gridAutosaveKey(gridId),
    gridColumnVisibilityKey(gridId),
    gridContextualColumnVisibilityOverridesKey(gridId),
    gridColumnOrderKey(gridId),
    ...extraAutosaveKeys,
  ];
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch { /* ignore quota / private mode */ }
  }
}

/**
 * @process persistAppliedGridState
 * Steps: 1. Write the full GridState snapshot to the canonical autosave key.
 *        2. Write columnVisibility and columnOrder to the DataGrid keys.
 *        3. Mirror the snapshot onto any legacy extra autosave keys.
 * Inputs: gridId, state, optional extraAutosaveKeys.
 * Outputs: void — every layout layer holds the same applied state.
 * Failure Handling: swallow Storage errors so in-memory apply still proceeds.
 * Idempotency: REQUIRED (last write wins).
 */
export function persistAppliedGridState(
  gridId: string,
  state: GridState,
  extraAutosaveKeys: string[] = [],
): void {
  const snapshot = cloneGridState(state);
  writeJson(gridAutosaveKey(gridId), snapshot);
  writeJson(gridColumnVisibilityKey(gridId), snapshot.columnVisibility ?? {});
  writeJson(gridColumnOrderKey(gridId), snapshot.columnOrder ?? []);
  for (const key of extraAutosaveKeys) {
    writeJson(key, snapshot);
  }
}

/**
 * @process completeColumnVisibility
 * Steps: 1. Start every known column as visible (true).
 *        2. Overlay stored true/false values so hidden columns stay hidden.
 * Inputs: stored map (may omit visible columns), optional knownColumnIds.
 * Outputs: A complete id→boolean map. Empty stored + no ids → {}.
 * Failure Handling: missing stored → all known ids true (never throws).
 * Idempotency: REQUIRED (pure).
 */
export function completeColumnVisibility(
  stored: Record<string, boolean> | undefined,
  knownColumnIds: string[] = [],
): Record<string, boolean> {
  const complete: Record<string, boolean> = {};
  for (const id of knownColumnIds) complete[id] = true;
  if (stored) {
    for (const [id, visible] of Object.entries(stored)) complete[id] = visible;
  }
  return complete;
}

/**
 * @process reconcileColumnLayout
 * Steps: 1. Remove reserved and duplicate supplied column IDs.
 *        2. Keep visibility for surviving columns and default missing values from `hidden`.
 *        3. Keep surviving saved order, then append new columns in supplied order.
 * Inputs: Supplied columns, saved visibility map, and saved column order.
 * Outputs: Complete visibility and ordering for currently supplied data columns.
 * Failure Handling: Retired and reserved IDs are omitted from both outputs.
 * Idempotency: REQUIRED (pure).
 */
export function reconcileColumnLayout(
  columns: readonly { id: string; hidden?: boolean }[],
  visibility: Record<string, boolean> = {},
  order: readonly string[] = [],
): { visibility: Record<string, boolean>; order: string[] } {
  const supplied: { id: string; hidden?: boolean }[] = [];
  const knownIds = new Set<string>();
  for (const column of columns) {
    if (column.id === '_actions' || column.id === '_select' || knownIds.has(column.id)) continue;
    knownIds.add(column.id);
    supplied.push(column);
  }

  const reconciledVisibility: Record<string, boolean> = {};
  for (const column of supplied) {
    reconciledVisibility[column.id] = Object.prototype.hasOwnProperty.call(visibility, column.id)
      ? visibility[column.id]
      : !column.hidden;
  }

  const reconciledOrder: string[] = [];
  const orderedIds = new Set<string>();
  for (const id of order) {
    if (knownIds.has(id) && !orderedIds.has(id)) {
      orderedIds.add(id);
      reconciledOrder.push(id);
    }
  }
  for (const { id } of supplied) {
    if (!orderedIds.has(id)) {
      orderedIds.add(id);
      reconciledOrder.push(id);
    }
  }

  return { visibility: reconciledVisibility, order: reconciledOrder };
}

/**
 * @process retainUnsuppliedColumnLayout
 * Steps: Keep reconciled values, retaining saved slots only for IDs never supplied in this mount.
 * Inputs: Reconciled layout, prior visibility/order, and IDs supplied during this mount.
 * Outputs: Reconciled layout with pending saved visibility and order slots preserved.
 * Failure Handling: Invalid visibility values and reserved IDs are ignored.
 * Idempotency: REQUIRED (pure).
 */
export function retainUnsuppliedColumnLayout(
  reconciled: { visibility: Record<string, boolean>; order: string[] },
  previous: { visibility?: Record<string, boolean>; order?: readonly string[] },
  everSuppliedIds: ReadonlySet<string>,
): { visibility: Record<string, boolean>; order: string[] } {
  const isPending = (id: string) => id !== '_actions' && id !== '_select' && !everSuppliedIds.has(id);
  const pendingVisibility: Record<string, boolean> = {};
  for (const [id, visible] of Object.entries(previous.visibility ?? {})) {
    if (isPending(id) && typeof visible === 'boolean') pendingVisibility[id] = visible;
  }
  const knownIds = new Set(reconciled.order);
  const queue = [...reconciled.order];
  const order: string[] = [];
  const slotIds = new Set<string>();
  for (const id of previous.order ?? []) {
    if (slotIds.has(id)) continue;
    slotIds.add(id);
    if (isPending(id)) order.push(id);
    else if (knownIds.has(id) && queue.length > 0) order.push(queue.shift()!);
  }
  order.push(...queue);
  return { visibility: { ...pendingVisibility, ...reconciled.visibility }, order };
}

function isFactoryHiddenColumn(id: string, factoryHidden: Record<string, boolean>): boolean {
  return factoryHidden[id] === false || id.startsWith('sa_');
}

/**
 * @process resolveTemplateColumnVisibility
 * Steps: 1. Empty / factory-only maps (no real columns recorded) → show every
 *           known column except factory-hidden accessors.
 *        2. Otherwise overlay the stored true/false flags onto a full
 *           show-all map — omitted columns stay VISIBLE, matching the
 *           legacy TanStack contract (`columnVisibility[id] !== false`).
 * Inputs: stored visibility, knownColumnIds, optional factoryHidden.
 * Outputs: A complete id→boolean map bound to one named template.
 * Failure Handling: missing stored → factory show-all (never throws).
 * Idempotency: REQUIRED (pure).
 *
 * Notes: save paths always persist COMPLETE maps (a one-column template is
 * stored `{a: true, b: false, …}`, which carries explicit falses and round-
 * trips through the overlay). A true-only stored map therefore means "every
 * column was visible when saved" or is sparse legacy data — both must keep
 * omitted columns visible rather than be reinterpreted as an allowlist, or
 * pre-existing templates would hide columns they never hid before.
 */
export function resolveTemplateColumnVisibility(
  stored: Record<string, boolean> | undefined,
  knownColumnIds: string[] = [],
  factoryHidden: Record<string, boolean> = {},
): Record<string, boolean> {
  const map = stored ?? {};
  const dataEntries = Object.entries(map).filter(([id]) => !isFactoryHiddenColumn(id, factoryHidden));
  const isFactoryMap = dataEntries.length === 0;
  if (isFactoryMap) {
    if (knownColumnIds.length === 0) return { ...factoryHidden };
    return completeColumnVisibility(factoryHidden, knownColumnIds);
  }
  return completeColumnVisibility({ ...factoryHidden, ...map }, knownColumnIds);
}

/**
 * @process snapshotLiveGridLayout
 * Steps: 1. Clone the in-memory GridState (sort, filters, search, grouping).
 *        2. Overlay DataGrid's live columnVisibility/columnOrder from
 *           localStorage so a Columns change is captured even when the parent
 *           autosave debounce has not flushed.
 * Inputs: gridId, currentState (parent ref / autosave snapshot).
 * Outputs: A complete GridState suitable to persist as a named template.
 * Failure Handling: missing/corrupt live keys → keep cloned currentState fields.
 * Idempotency: REQUIRED (pure aside from Storage reads).
 */
export function snapshotLiveGridLayout(
  gridId: string,
  currentState: Partial<GridState> = {},
  knownColumnIds: string[] = [],
): GridState {
  const snapshot: GridState = cloneGridState({
    columnVisibility: currentState.columnVisibility ?? {},
    columnOrder: currentState.columnOrder ?? [],
    sorting: currentState.sorting ?? [],
    filterValues: currentState.filterValues ?? {},
    pageSize: currentState.pageSize ?? 25,
    searchText: currentState.searchText ?? '',
    groupByColumns: currentState.groupByColumns,
    activeQuery: currentState.activeQuery,
    columnFilters: currentState.columnFilters ?? [],
  });
  try {
    const visRaw = localStorage.getItem(gridColumnVisibilityKey(gridId));
    if (visRaw) {
      const parsed = JSON.parse(visRaw) as Record<string, boolean>;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        snapshot.columnVisibility = parsed;
      }
    }
  } catch { /* keep cloned visibility */ }
  try {
    const orderRaw = localStorage.getItem(gridColumnOrderKey(gridId));
    if (orderRaw) {
      const parsed = JSON.parse(orderRaw) as unknown;
      if (Array.isArray(parsed)) snapshot.columnOrder = parsed as string[];
    }
  } catch { /* keep cloned order */ }
  snapshot.columnVisibility = resolveTemplateColumnVisibility(snapshot.columnVisibility, knownColumnIds);
  return snapshot;
}

/**
 * @contract ResolveFactoryGridStateOptions
 * Purpose: Page-level defaults that shape the factory GridState for "Reset to defaults".
 * Fields: defaultPageSize, defaultSorting, defaultGroupBy, fallbackColumnVisibility.
 * Rules: Never reads templates. Empty columnVisibility/columnOrder mean show every
 *        non-hidden column in natural order. fallbackColumnVisibility is for
 *        columns that must stay hidden (e.g. Locations service-area accessors).
 * Sync: local-only; factory values come from GridPageShell / page props.
 */
export interface ResolveFactoryGridStateOptions {
  defaultPageSize?: number;
  defaultSorting?: GridState['sorting'];
  defaultGroupBy?: string[];
  fallbackColumnVisibility?: Record<string, boolean>;
}

/**
 * @process resolveFactoryGridState
 * Steps: 1. Build an empty GridState honoring page-level sort/group/page-size.
 *        2. Apply fallbackColumnVisibility when the page has columns that must
 *           stay hidden (never infer from starred or system templates).
 * Inputs: ResolveFactoryGridStateOptions.
 * Outputs: A complete GridState suitable for DataGrid initialState after Reset.
 * Failure Handling: missing options → empty factory (never throws).
 * Idempotency: REQUIRED (pure).
 */
export function resolveFactoryGridState(
  options: ResolveFactoryGridStateOptions = {},
): GridState {
  return {
    columnVisibility: options.fallbackColumnVisibility ?? {},
    columnOrder: [],
    sorting: options.defaultSorting ?? [],
    filterValues: {},
    pageSize: options.defaultPageSize ?? 25,
    searchText: '',
    groupByColumns: options.defaultGroupBy ?? [],
    columnFilters: [],
  };
}

/**
 * @contract ResolveDefaultGridStateOptions
 * Purpose: Optional inputs that shape a GridState from a starred or system template.
 * Fields: templates, defaultPageSize, defaultSorting, defaultGroupBy, fallbackColumnVisibility.
 * Rules: Starred (isDefault) template wins over the first system template; empty
 *        arrays/objects are valid "show everything / natural order" values.
 *        Not used by "Reset to defaults" — use resolveFactoryGridState instead.
 * Sync: local-only; templates themselves may be seeded or user-saved in localStorage.
 */
export interface ResolveDefaultGridStateOptions extends ResolveFactoryGridStateOptions {
  templates?: readonly GridTemplate[];
}

/**
 * @process resolveDefaultGridState
 * Steps: 1. Prefer the starred non-archived template.
 *        2. Else the first non-archived system template.
 *        3. Else a factory empty state honoring page-level defaults.
 * Inputs: ResolveDefaultGridStateOptions.
 * Outputs: A complete GridState suitable for DataGrid initialState.
 * Failure Handling: missing templates → factory default (never throws).
 * Idempotency: REQUIRED (pure).
 */
export function resolveDefaultGridState(
  options: ResolveDefaultGridStateOptions = {},
): GridState {
  const defaultTpl = options.templates?.find((t) => t.isDefault && !t.isArchived)
    ?? options.templates?.find((t) => t.scope === 'system' && !t.isArchived);
  if (defaultTpl?.state) {
    return {
      ...defaultTpl.state,
      sorting: defaultTpl.state.sorting ?? options.defaultSorting ?? [],
      groupByColumns: defaultTpl.state.groupByColumns ?? options.defaultGroupBy ?? [],
      pageSize: defaultTpl.state.pageSize ?? options.defaultPageSize ?? 25,
    };
  }
  return resolveFactoryGridState(options);
}

/**
 * @contract ResolveInitialGridStateOptions
 * Purpose: Mount-time inputs that choose between autosave, a pending starred
 *          default, and page factory values.
 * Fields:  gridId, templates, extraAutosaveKeys, contextualColumnDefaults, page factory options.
 * Rules:   A pending starred default (Star without Apply) supersedes leftover
 *          autosave once. Explicit Apply/Reset clear the pending bookmark, so
 *          remount keeps that layout. First visit with no autosave uses page
 *          factory values, not the first system template.
 * Sync:    localStorage only.
 */
export interface ResolveInitialGridStateOptions extends ResolveDefaultGridStateOptions {
  gridId: string;
  extraAutosaveKeys?: string[];
  contextualColumnDefaults?: Record<string, boolean>;
}

function readJsonGridState(key: string): Partial<GridState> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<GridState>;
  } catch {
    return null;
  }
}

/**
 * @process loadPersistedGridState
 * Steps: 1. Read the canonical autosave key.
 *        2. Fall back through extra/legacy autosave keys.
 * Inputs: gridId, extraAutosaveKeys.
 * Outputs: Partial GridState or null.
 * Failure Handling: corrupt JSON → next key, then null.
 * Idempotency: REQUIRED.
 */
export function loadPersistedGridState(
  gridId: string,
  extraAutosaveKeys: string[] = [],
): Partial<GridState> | null {
  const keys = [gridAutosaveKey(gridId), ...extraAutosaveKeys];
  for (const key of keys) {
    const saved = readJsonGridState(key);
    if (saved) return saved;
  }
  return null;
}

function completeGridState(
  saved: Partial<GridState>,
  options: ResolveFactoryGridStateOptions = {},
): GridState {
  return {
    columnVisibility: saved.columnVisibility ?? options.fallbackColumnVisibility ?? {},
    columnOrder: saved.columnOrder ?? [],
    sorting: saved.sorting ?? options.defaultSorting ?? [],
    filterValues: saved.filterValues ?? {},
    pageSize: saved.pageSize ?? options.defaultPageSize ?? 25,
    searchText: saved.searchText ?? '',
    groupByColumns: saved.groupByColumns ?? options.defaultGroupBy,
    activeQuery: saved.activeQuery,
    columnFilters: saved.columnFilters ?? [],
  };
}

function starredDefaultTemplate(templates: readonly GridTemplate[] | undefined): GridTemplate | undefined {
  return templates?.find((t) => t.isDefault && !t.isArchived);
}

/**
 * @process resolveInitialGridState
 * Steps: 1. If a pending starred default matches the current star, return that
 *           template via resolveDefaultGridState.
 *        2. Else restore completed autosave when present.
 *        3. Else page factory values.
 * Inputs: ResolveInitialGridStateOptions.
 * Outputs: Complete GridState for first paint. Pure read of localStorage.
 * Failure Handling: missing storage → factory path (never throws).
 * Idempotency: REQUIRED.
 */
export function resolveInitialGridState(
  options: ResolveInitialGridStateOptions,
): GridState {
  const {
    gridId,
    templates = [],
    extraAutosaveKeys = [],
    defaultPageSize,
    defaultSorting,
    defaultGroupBy,
    fallbackColumnVisibility,
  } = options;
  const factoryOptions = { defaultPageSize, defaultSorting, defaultGroupBy, fallbackColumnVisibility };
  const starred = starredDefaultTemplate(templates);
  const pending = readPendingDefault(gridId);
  if (starred?.state && pending?.id === starred._id) {
    return resolveDefaultGridState({ templates: [starred], ...factoryOptions });
  }
  const saved = loadPersistedGridState(gridId, extraAutosaveKeys);
  if (saved) {
    return completeGridState(saved, factoryOptions);
  }
  return resolveFactoryGridState(factoryOptions);
}

/**
 * @process initializePersistedGridState
 * Steps: 1. Resolve the mount layout.
 *        2. When a pending star was honored, derive contextual override markers
 *           from its original state, persist autosave/applied-template metadata, and consume the bookmark.
 * Inputs: ResolveInitialGridStateOptions.
 * Outputs: Complete GridState; may write localStorage when consuming a star.
 * Failure Handling: storage writes are swallowed by persist helpers.
 * Idempotency: second call with no pending bookmark is a pure read.
 */
export function initializePersistedGridState(
  options: ResolveInitialGridStateOptions,
): GridState {
  const state = resolveInitialGridState(options);
  const starred = starredDefaultTemplate(options.templates);
  const pending = readPendingDefault(options.gridId);
  if (starred && pending?.id === starred._id) {
    if (options.contextualColumnDefaults) {
      const overrides = readContextualColumnVisibilityOverrides(options.gridId);
      const templateVisibility = starred.state.columnVisibility ?? {};
      for (const id of Object.keys(options.contextualColumnDefaults)) {
        if (Object.prototype.hasOwnProperty.call(templateVisibility, id)) overrides[id] = templateVisibility[id];
        else delete overrides[id];
      }
      writeContextualColumnVisibilityOverrides(options.gridId, overrides);
    }
    persistAppliedGridState(options.gridId, state, options.extraAutosaveKeys ?? []);
    writeAppliedTemplate(options.gridId, { id: starred._id, name: starred.name });
    writePendingDefault(options.gridId, null);
  }
  return state;
}

/**
 * @process resolveMountGridState
 * Steps: 1. initializePersistedGridState (may consume a pending star).
 *        2. If any autosave exists after that, return the complete snapshot.
 *        3. Else return a first-visit Partial so DataGrid can still hydrate
 *           independently persisted colvis/colorder.
 * Inputs: ResolveInitialGridStateOptions.
 * Outputs: Partial GridState for React initial state.
 * Failure Handling: never throws.
 * Idempotency: REQUIRED after the pending bookmark is consumed.
 */
export function resolveMountGridState(
  options: ResolveInitialGridStateOptions,
): Partial<GridState> {
  const state = initializePersistedGridState(options);
  if (loadPersistedGridState(options.gridId, options.extraAutosaveKeys ?? [])) {
    return state;
  }
  const partial: Partial<GridState> = {};
  if (state.sorting.length > 0) partial.sorting = state.sorting;
  if (state.groupByColumns && state.groupByColumns.length > 0) partial.groupByColumns = state.groupByColumns;
  if (Object.keys(state.columnVisibility).length > 0) partial.columnVisibility = state.columnVisibility;
  if (state.columnOrder.length > 0) partial.columnOrder = state.columnOrder;
  if (state.activeQuery) partial.activeQuery = state.activeQuery;
  return partial;
}
